import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React from "react";
import { fmap } from "../../../utils/fmap";
import {
  CaregiverRankBedsideBuddyIcon,
  CaregiverRankHarmonyHuntsmanIcon,
  CaregiverRankHomeHeroIcon,
  CaregiverRankPatientPalIcon,
  CaregiverRankRookieCaregiverIcon,
  CaregiverRankShiftSuperstarIcon,
  CaregiverRankVisitVirtuosoIcon,
  CaregiverRankWellnessWarriorIcon,
} from "../caregiver-rank-icons";
import CareMomentsAnimatedPlus from "./CareMomentsAnimatedPlus";
import CareMomentsCaregiverRank from "./CareMomentsCaregiverRank";
import Parallax from "./Parallax";

interface Props {
  scrollRef: React.RefObject<HTMLElement>;
}

export default function CareMomentsIntroAnimation({ scrollRef }: Props) {
  return (
    <Root>
      <CareMomentsAnimatedPlus scrollRef={scrollRef} />

      <Bubble positioning={{ top: 50, right: 10 }} delay={2} repeat={0}>
        <Parallax scrollRef={scrollRef} effect="translate" factor={0.2}>
          <Parallax scrollRef={scrollRef} effect="blur" factor={0.1}>
            <CareMomentsCaregiverRank icon={<CaregiverRankBedsideBuddyIcon />} size="xs" />
          </Parallax>
        </Parallax>
      </Bubble>

      <Bubble positioning={{ top: 5, left: -2 }} delay={2.5} repeat={0}>
        <Parallax scrollRef={scrollRef} effect="translate" factor={0.3}>
          <Parallax scrollRef={scrollRef} effect="blur" factor={0.1}>
            <CareMomentsCaregiverRank icon={<CaregiverRankHarmonyHuntsmanIcon />} size="xs" />
          </Parallax>
        </Parallax>
      </Bubble>

      <Bubble positioning={{ bottom: -5, right: 20 }} delay={3} repeat={0}>
        <Parallax scrollRef={scrollRef} effect="translate" factor={0.4}>
          <Parallax scrollRef={scrollRef} effect="blur" factor={0.1}>
            <CareMomentsCaregiverRank icon={<CaregiverRankWellnessWarriorIcon />} />
          </Parallax>
        </Parallax>
      </Bubble>

      <Bubble positioning={{ bottom: 10, left: 30 }} delay={3.5} repeat={0}>
        <Parallax scrollRef={scrollRef} effect="translate" factor={0.2}>
          <Parallax scrollRef={scrollRef} effect="blur" factor={0.1}>
            <CareMomentsCaregiverRank icon={<CaregiverRankPatientPalIcon />} size="sm" />
          </Parallax>
        </Parallax>
      </Bubble>

      <Bubble duration={1} positioning={{ top: 15, right: 5 }} delay={3} repeatDelay={5}>
        <Parallax scrollRef={scrollRef} effect="translate" factor={-0.2}>
          <CareMomentsCaregiverRank
            amount={19.95}
            icon={<CaregiverRankHomeHeroIcon />}
            direction="right"
            size="lg"
          />
        </Parallax>
      </Bubble>

      <Bubble duration={1} positioning={{ top: 60, right: 55 }} delay={3.5} repeatDelay={5}>
        <Parallax scrollRef={scrollRef} effect="translate" factor={-0.4}>
          <CareMomentsCaregiverRank
            amount={19.55}
            icon={<CaregiverRankVisitVirtuosoIcon />}
            size="sm"
          />
        </Parallax>
      </Bubble>

      <Bubble duration={1} positioning={{ top: 25, right: 60 }} delay={8} repeatDelay={5}>
        <Parallax scrollRef={scrollRef} effect="translate" factor={-0.2}>
          <CareMomentsCaregiverRank
            amount={19.85}
            icon={<CaregiverRankShiftSuperstarIcon />}
            direction="left"
          />
        </Parallax>
      </Bubble>

      <Bubble duration={1} positioning={{ top: 70, right: 10 }} delay={9} repeatDelay={5}>
        <Parallax scrollRef={scrollRef} effect="translate" factor={-0.4}>
          <CareMomentsCaregiverRank
            amount={20.05}
            icon={<CaregiverRankRookieCaregiverIcon />}
            direction="left"
          />
        </Parallax>
      </Bubble>
    </Root>
  );
}

const rootAnimation = keyframes`
    from { height: 45vh };
`;

const Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 35vh;
  overflow: hidden;
  animation: ${rootAnimation} 0.5s ease 5s backwards;
`;

function Bubble(props: {
  delay?: number;
  repeat?: number;
  repeatDelay?: number;
  duration?: number;
  positioning: { top?: number; bottom?: number; left?: number; right?: number };
  children: React.ReactNode;
}) {
  return (
    <motion.div
      style={{
        position: "absolute",
        transform: "translate(-50%, -50%)",
        top: fmap(props.positioning.top, (x) => `${x}%`) ?? undefined,
        right: fmap(props.positioning.right, (x) => `${x}%`) ?? undefined,
        bottom: fmap(props.positioning.bottom, (x) => `${x}%`) ?? undefined,
        left: fmap(props.positioning.left, (x) => `${x}%`) ?? undefined,
      }}
      initial={{
        scale: 0.2,
        opacity: 0,
        filter: "blur(30px)",
      }}
      animate={{
        scale: 1,
        opacity: 1,
        filter: "blur(0px)",
      }}
      transition={{
        duration: props.duration ?? 3,
        type: "keyframes",
        delay: props.delay,
        repeat: props.repeat ?? Infinity,
        repeatType: "reverse",
        repeatDelay: props.repeatDelay,
      }}
    >
      {props.children}
    </motion.div>
  );
}
